/*
 * Day arithmetic and labels of the date-range field (mock _kit/create.html + shell.js fmtDate /
 * fmtRange). Days travel as `YYYY-MM-DD` strings: they compare lexically, and `T12:00` keeps a
 * DST switch from moving them to the day before. Month and weekday names come from Intl in the
 * current language — the dayjs locales are disabled app-wide.
 */

const pad = (n) => String(n).padStart(2, '0')

export const isoOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

export const atNoon = (iso) => new Date(`${iso}T12:00`)

export const todayIso = () => isoOf(new Date())

export const addDays = (iso, n) => {
    const date = atNoon(iso)
    date.setDate(date.getDate() + n)
    return isoOf(date)
}

/** Inclusive number of days between two ISO days (same day → 1). */
export const dayCount = (startIso, endIso) => Math.round((atNoon(endIso) - atNoon(startIso)) / 864e5) + 1

const LOCALES = { en: 'en-GB', fr: 'fr-FR', pt: 'pt-PT' }

export const localeOf = (language) => LOCALES[String(language || 'en').slice(0, 2)] || LOCALES.en

export const formatDate = (iso, language) =>
    atNoon(iso).toLocaleDateString(localeOf(language), { day: 'numeric', month: 'short', year: 'numeric' })

/** "17 - 19 Jul 2026" inside one month, "30 Jul 2026 - 2 Aug 2026" otherwise. */
export const formatRange = (startIso, endIso, language) => {
    if (!endIso || endIso === startIso) return formatDate(startIso, language)
    const start = atNoon(startIso)
    const end = atNoon(endIso)
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        const month = start.toLocaleDateString(localeOf(language), { month: 'short' })
        return `${start.getDate()} - ${end.getDate()} ${month} ${start.getFullYear()}`
    }
    return `${formatDate(startIso, language)} - ${formatDate(endIso, language)}`
}

export const monthTitle = (year, month, language) =>
    new Date(year, month, 1).toLocaleDateString(localeOf(language), { month: 'long', year: 'numeric' })

/** Two-letter weekday labels, Monday first (2024-01-01 was a Monday). */
export const weekdayLabels = (language) =>
    Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 1 + i).toLocaleDateString(localeOf(language), { weekday: 'short' }).slice(0, 2),
    )

/** Grid data of a month: blank cells before the 1st (Monday-first) and the ISO of every day. */
export const monthGrid = (year, month) => {
    const leading = (new Date(year, month, 1).getDay() + 6) % 7
    const count = new Date(year, month + 1, 0).getDate()
    const days = Array.from({ length: count }, (_, i) => `${year}-${pad(month + 1)}-${pad(i + 1)}`)
    return { leading, days }
}

export const nextMonth = ({ year, month }, n = 1) => {
    const date = new Date(year, month + n, 1)
    return { year: date.getFullYear(), month: date.getMonth() }
}

export const monthOf = (iso) => {
    const date = atNoon(iso)
    return { year: date.getFullYear(), month: date.getMonth() }
}
